import React, { useEffect, useMemo, useState } from "react";
import useInventoryApi from "./useInventoryApi";
import { errorText, listDeliveries, postDelivery } from "./useChemicalsApi";
import ConfirmCard from "../../common/ConfirmCard";
import "./Inventory.css";

// D2 receipt rail UI: upload a supplier receipt (photo/PDF) or paste text ->
// Bedrock extract -> PROPOSED lines rendered as ConfirmCards -> per-line
// confirm / new-product / reject -> receive into a location (StockLedger).
export default function ReceiptsPage() {
  const { uploadReceiptFile, ingestReceipt, confirmReceipt, listLocations } = useInventoryApi();
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [actions, setActions] = useState({}); // lineId -> 'confirm'|'new'|'reject'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  // Every delivery on file — scanned or typed — newest first; drafts are typed deliveries not yet posted.
  const [deliveries, setDeliveries] = useState(null);
  const [dlMsg, setDlMsg] = useState("");
  const loadDeliveries = async () => {
    try { setDeliveries(await listDeliveries()); } catch (e2) { setDlMsg(errorText(e2, "Couldn't load deliveries.")); }
  };

  useEffect(() => {
    listLocations().then((r) => setLocations(Array.isArray(r) ? r : r?.results || [])).catch(() => {});
    loadDeliveries();
  }, [listLocations]); // eslint-disable-line react-hooks/exhaustive-deps

  const postDraft = async (d) => {
    setDlMsg("");
    try {
      const out = await postDelivery(d.id, {});
      setDlMsg(`Posted ${d.supplier || `#${d.id}`}: ${out.summary.received} line(s) to ${out.summary.location.name}.`);
      loadDeliveries();
    } catch (e2) { setDlMsg(errorText(e2, "Couldn't post that delivery.")); }
  };
  const STATUS_WORDS = { draft: "Draft (typed, not posted)", extracted: "Read, awaiting review", confirmed: "Posted", rejected: "Rejected", failed: "Read failed" };

  const resetTo = (r) => {
    setReceipt(r);
    setDone(null);
    setActions(Object.fromEntries((r.lines || []).map((l) => [l.id, l.matched_product ? "confirm" : "new"])));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const uploaded = await uploadReceiptFile(file);
      const r = await ingestReceipt({ source_file_uuid: uploaded.file_uuid });
      resetTo(r);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Extraction failed.");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const onPaste = async () => {
    const text = window.prompt("Paste receipt text:");
    if (!text) return;
    setBusy(true); setErr("");
    try { resetTo(await ingestReceipt({ text })); }
    catch (e2) { setErr(e2?.response?.data?.detail || "Extraction failed."); }
    finally { setBusy(false); }
  };

  const setLineAction = (id, a) => setActions((m) => ({ ...m, [id]: a }));

  const summary = useMemo(() => {
    const vals = Object.values(actions);
    return {
      receive: vals.filter((a) => a === "confirm" || a === "new").length,
      reject: vals.filter((a) => a === "reject").length,
    };
  }, [actions]);

  const submit = async () => {
    if (!locationId) { setErr("Pick a location to receive into."); return; }
    setBusy(true); setErr("");
    try {
      const res = await confirmReceipt(receipt.id, { location_id: Number(locationId), line_actions: actions });
      setDone(res.summary);
      setReceipt(res.receipt || receipt);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Confirm failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="inventory-page">
      <section className="chem-card" style={{ marginBottom: "var(--s-3)" }}>
        <div className="chem-head"><div><h2>Deliveries</h2><div className="chem-sub">Scanned or typed, newest first. A draft is a typed delivery saved on the phone to finish later; posting writes its lines to stock.</div></div>
          {deliveries ? <div className="chem-stat"><div className="chem-stat__val">{deliveries.count}</div><div className="chem-stat__label">on file</div></div> : null}</div>
        {dlMsg ? <div className="chem-msg">{dlMsg}</div> : null}
        {deliveries === null ? <div className="chem-empty">Loading…</div> : deliveries.results.length === 0 ? <div className="chem-empty">No deliveries yet.</div> : (
          <div className="chem-table-wrap"><table className="chem-table">
            <thead><tr><th>When</th><th>Supplier</th><th>Invoice</th><th>Status</th><th className="num">Lines</th><th className="num">Line cost</th><th>By</th><th>Receipt</th><th /></tr></thead>
            <tbody>{deliveries.results.map((d) => (
              <tr key={d.id}>
                <td>{new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</td>
                <td>{d.supplier || "—"}</td><td>{d.invoice_number || "—"}</td>
                <td><span className={`chem-pill ${d.status === "confirmed" ? "chem-pill--ok" : d.status === "draft" || d.status === "extracted" ? "chem-pill--warn" : ""}`}>{STATUS_WORDS[d.status] || d.status}</span></td>
                <td className="num">{d.lines.length}</td><td className="num">{d.line_cost_total ? `$${d.line_cost_total.toFixed(2)}` : "—"}</td>
                <td>{d.created_by || "—"}</td>
                <td>{d.photo?.url ? <a href={d.photo.url} target="_blank" rel="noreferrer">{d.photo.name || "open"}</a> : "—"}</td>
                <td>{d.status === "draft" ? <button type="button" className="chem-btn" onClick={() => postDraft(d)}>Post to warehouse</button>
                     : d.status === "extracted" ? <button type="button" className="chem-btn" onClick={() => resetTo({ ...d, vendor_name: d.supplier, lines: d.lines.map((l) => ({ ...l, description: l.name, matched_product: l.product_id ? { id: l.product_id, name: l.product } : null })) })}>Review</button> : null}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </section>

      <header className="inventory-head">
        <div>
          <h1>Receipts</h1>
          <p className="muted">Upload a supplier receipt — we extract the line items for you to confirm into stock.</p>
        </div>
        <div className="filters">
          <label className="btn mini" style={{ cursor: "pointer" }}>
            Upload receipt
            <input type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={onFile} />
          </label>
          <button className="btn mini secondary" onClick={onPaste}>Paste text</button>
        </div>
      </header>

      {busy && <div className="inventory-row muted">Working…</div>}
      {err && <div className="inventory-card" style={{ padding: 12 }}><span className="error-text">{err}</span></div>}

      {done && (
        <div className="inventory-card" style={{ padding: 16 }}>
          <div className="value" style={{ fontSize: 15 }}>Received {done.received} line(s)</div>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            {done.created_products} new product(s) created, {done.rejected} rejected. Stock updated.
          </p>
        </div>
      )}

      {receipt && !done && (
        <>
          <section className="inventory-card" style={{ padding: 16 }}>
            <div className="row-between" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div>
                <strong>{receipt.vendor_name || "Receipt"}</strong>
                {receipt.invoice_number ? ` · #${receipt.invoice_number}` : ""}
                {receipt.invoice_date ? ` · ${receipt.invoice_date}` : ""}
                {receipt.total != null ? ` · $${receipt.total}` : ""}
              </div>
              <label className="muted" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Receive into
                <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  <option value="">Select location…</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name} ({l.kind})</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(receipt.lines || []).map((l) => {
              const act = actions[l.id];
              const matched = !!l.matched_product;
              return (
                <ConfirmCard
                  key={l.id}
                  title={l.description || l.supplier_sku}
                  subtitle={`${l.qty ?? "?"} ${l.uom || ""} · $${l.unit_cost ?? "?"}/unit${l.supplier_sku ? ` · SKU ${l.supplier_sku}` : ""}`}
                  meta={[
                    ...(l.epa_reg_number ? [{ label: "EPA", value: l.epa_reg_number }] : []),
                    ...(matched ? [{ label: "→", value: l.matched_product.name }] : []),
                  ]}
                  confidence={matched ? l.match_confidence : undefined}
                  badge={matched ? { text: `match · ${l.match_method}`, tone: "match" } : { text: "new product", tone: "new" }}
                  state={act === "reject" ? "rejected" : "pending"}
                  actions={[
                    ...(matched ? [{ label: act === "confirm" ? "✓ Confirm" : "Confirm", variant: act === "confirm" ? "primary" : "ghost", onClick: () => setLineAction(l.id, "confirm") }] : []),
                    { label: act === "new" ? "✓ New product" : "New product", variant: act === "new" ? "primary" : "ghost", onClick: () => setLineAction(l.id, "new") },
                    { label: "Reject", variant: "danger", onClick: () => setLineAction(l.id, "reject") },
                  ]}
                />
              );
            })}
          </div>

          <section className="inventory-card" style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <span className="muted">{summary.receive} to receive · {summary.reject} rejected</span>
            <button className="btn" onClick={submit} disabled={busy || !locationId}>
              Receive {summary.receive} line(s)
            </button>
          </section>
        </>
      )}

      {!receipt && !busy && (
        <div className="inventory-empty">
          <div className="value" style={{ fontSize: 15 }}>No receipt loaded</div>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Upload a supplier receipt (photo or PDF) — the line items extract for you to confirm into stock.
          </p>
        </div>
      )}
    </div>
  );
}
